import React, {
	forwardRef,
	lazy,
	memo,
	Suspense,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import { Button } from 'semantic-ui-react';

import Alert from '../../../Alert';
import CollectionLabelActions from './CollectionLabelActions';
import CollectionModal from './CollectionModal';
import Table from '../../../Table';
import TableRowActions from '../../../Table/Cells/TableRowActions';

import styles from '../../document-modal.module.css';

const CollectionModule = forwardRef( (
	{
		admin,
		moduleData,
		collectionLabel,
		instructions,
		itemLabel = 'Item',
		labelComponent,
		row,
		sections,
		ui,
		config: {
			allowAdd = true,
			allowDelete = true,
			allowEdit = true
		} = {}
	},
	ref
) => {
	const hasChanged = useRef( false );
	const [ data, setData ] = useState( JSON.parse( moduleData.data ) );
	const [ selectedItem, setSelectedItem ] = useState( null );
	const [ collectionModal, setCollectionModal ] = useState( false );
	const [ deleteConfirm, setDeleteConfirm ] = useState( false );
	const [ itemToDelete, setItemToDelete ] = useState( {} );

	const closeCollectionModal = () => {
		setSelectedItem( null );
		setCollectionModal( false );
	};

	const sequences = useMemo( () => [ ...new Set( data.map( ( { seq } ) => seq ) ) ], [ data ] );

	const updateModuleData = useCallback( () => {
		if ( hasChanged.current ) {
			hasChanged.current = false;

			return {
				...moduleData,
				data: JSON.stringify( data )
			};
		}

		return null;
	}, [
		data,
		moduleData
	] );

	useImperativeHandle( ref, () => ( { updateModuleData } ), [ updateModuleData ] );

	const Label = lazy( () => import( `./CollectionLabels/${ labelComponent }` ).catch( () => ( { default: null } ) ) );

	// collection CRUD

	const handleEditCollectionItem = useCallback( seq => {
		const selectedItem = data.filter( item => item.seq === seq );

		setSelectedItem( selectedItem );
		setCollectionModal( true );
	}, [ data ] );

	const handleDeleteCollectionItem = useCallback( seq => {
		setData( prev => prev.filter( item => item.seq !== seq ) );

		hasChanged.current = true;
	}, [] );

	const handleAddCollectionButton = useCallback( () => {
		setCollectionModal( true );
	}, [] );

	const handleUpdate = useCallback( newCollection => {
		setData( prev => Array.from( [ ...prev, ...newCollection ]
			.reduce( ( acc, item ) => acc.set( `${ item.questionId }-${ item.seq }`, item ), new Map() )
			.values() ) );

		hasChanged.current = true;
	}, [] );

	const handleCreate = useCallback( newCollection => {
		setData( prev => {
			const seq = Math.max( 0, ...sequences ) + 1;
			const updatedCollection = newCollection.map( collection => ( {
				...collection,
				seq
			} ) );

			return [
				...prev,
				...updatedCollection
			];
		} );

		hasChanged.current = true;
	}, [ sequences ] );

	const confirmDelete = useCallback( seq => {
		setItemToDelete( seq );
		setDeleteConfirm( true );
	}, [] );

	const mapCollection = useCallback( () => sequences.map( seq => {
		let collection = data.filter( item => item.seq === seq );

		return (
			<div
				className={ styles.collectionLabel }
				key={ seq }
			>
				{ Label.default === null
					? <p>{ itemLabel }</p> // create a component for this!
					: <Label
						data={ collection }
						ui={ ui }
					/>
				}
				<CollectionLabelActions
					deletable={ allowDelete }
					editable={ allowEdit }
					onEdit={ handleEditCollectionItem }
					onDelete={ confirmDelete }
					id={ seq }
				/>
			</div>
		);
	} ), [
		Label.default,
		allowDelete,
		allowEdit,
		confirmDelete,
		data,
		handleEditCollectionItem,
		itemLabel,
		sequences,
		ui
	] );

	// table

	const tableData = useMemo( () => sequences.map( seq => {
		const collection = data.filter( item => item.seq === seq );

		return collection.reduce( (
			acc,
			{
				optionId,
				questionId,
				value
			}
		) => {
			const {
				label,
				options,
				type
			} = ui.find( ( { id } ) => id === questionId );

			return {
				...acc,
				[ label ]: type === 6
					? options[ optionId ]
					: value
			};
		}, { seq } ); // seq is used to access the record when editing!!!!
	}, {} ), [
		data,
		sequences,
		ui
	] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const onEdit = ( { seq } ) => {
			handleEditCollectionItem( seq );
		};

		const onDelete = ( { seq } ) => {
			confirmDelete( seq );
		};

		const actions = [
			{
				action: onEdit,
				componentProps: { color: 'pink' },
				icon: 'pencil',
				permission: true,
				text: `Edit ${ itemLabel }`
			},
			{
				action: onDelete,
				componentProps: { color: 'red' },
				icon: 'times',
				permission: true,
				text: `Delete ${ itemLabel }`
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		confirmDelete,
		handleEditCollectionItem,
		itemLabel
	] );

	const columns = useMemo( () => [
		...row.map( i => {
			const {
				id,
				label
			} = ui[ i ];

			return {
				id,
				accessor: row => row[ label ] ?? '',
				Header: label,
				Cell: ( { value } ) => value ?? '',
				minWidth: 60
			};
		} ),
		{
			id: 'id_actions',
			Cell: tableRowActionsCell,
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		}
	], [
		row,
		tableRowActionsCell,
		ui
	] );

	// delete
	const handleDelete = useCallback( () => {
		handleDeleteCollectionItem( itemToDelete );
	}, [
		handleDeleteCollectionItem,
		itemToDelete
	] );

	const deleteConfirmMessage = useMemo( () => <span>{ `Are you sure you would like to delete this ${ itemLabel.toLowerCase() }?` }</span>, [ itemLabel ] );

	const deleteConfirmCloseModal = useCallback( () => setDeleteConfirm( false ), [] );

	return (
		<>
			<div className={ styles.collection }>
				<div className={ styles.collectionHeader }>
					<h3>{ collectionLabel }</h3>
				</div>
				{ instructions
					? <div className={ styles.instructions }>
						<div dangerouslySetInnerHTML={ { __html: instructions } } />
					</div>
					: null
				}
				<div className={ styles.addCollectionButton }>
					{ allowAdd
						? <Button
							content={ `Add New ${ itemLabel }` }
							color="pink"
							onClick={ handleAddCollectionButton }
							size="small"
						/>
						: null
					}
				</div>
				<Suspense fallback={ null }>
					<div>
						{
							labelComponent === 'Table'
								? <Table
									columns={ columns }
									data={ tableData }
									showFilters={ false }
								/>
								: mapCollection()
						}
					</div>
				</Suspense>
			</div>
			<CollectionModal
				admin={ admin }
				data={ selectedItem || [] }
				instructions={ instructions }
				label={ itemLabel }
				closeModal={ closeCollectionModal }
				onSubmit={ selectedItem
					? handleUpdate
					: handleCreate
				}
				open={ collectionModal }
				sections={ sections }
				ui={ ui }
			/>
			<Alert
				action={ handleDelete }
				name="collection-module-delete-confirm-alert"
				message={ deleteConfirmMessage }
				open={ deleteConfirm }
				closeModal={ deleteConfirmCloseModal }
				type="delete"
			/>
		</>
	);
} );

CollectionModule.displayName = 'CollectionModule';

CollectionModule.propTypes = {
	admin: PropTypes.bool,
	collectionLabel: PropTypes.string.isRequired,
	config: PropTypes.shape( {
		allowAdd: PropTypes.bool,
		allowDelete: PropTypes.bool,
		allowEdit: PropTypes.bool
	} ),
	instructions: PropTypes.string,
	itemLabel: PropTypes.string,
	labelComponent: PropTypes.string.isRequired,
	moduleData: PropTypes.object.isRequired,
	row: PropTypes.array,
	sections: PropTypes.array.isRequired,
	ui: PropTypes.array.isRequired
};

export default memo( CollectionModule );
